import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, Lang, getActiveLang } from '../../i18n';

const LABELS: Record<Lang, { short: string; full: string }> = {
  en: { short: 'EN', full: 'English' },
  ru: { short: 'RU', full: 'Русский' },
};

/** `tone` picks the color surface: "default" = primary-blue on light, "onDark"
 *  = white on a dark/dusk backdrop (the /spirit-box desktop navbar). */
type Tone = 'default' | 'onDark';

export function LanguageToggle({ tone = 'default' }: { tone?: Tone }) {
  const { i18n, t } = useTranslation('ui');
  const active = getActiveLang();
  const onDark = tone === 'onDark';

  return (
    <div
      role="group"
      aria-label={t('language')}
      className={[
        'inline-flex items-center gap-1 text-eyebrow font-medium tracking-eyebrow',
        onDark ? 'text-white/75' : 'text-primary/70',
      ].join(' ')}
    >
      {SUPPORTED_LANGS.map((lng, i) => {
        const isActive = lng === active;
        return (
          <React.Fragment key={lng}>
            {i > 0 && (
              <span aria-hidden className={onDark ? 'text-white/40' : 'text-primary/30'}>
                /
              </span>
            )}
            <button
              type="button"
              aria-pressed={isActive}
              aria-label={LABELS[lng].full}
              onClick={() => i18n.changeLanguage(lng)}
              className={[
                'rounded transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                onDark
                  ? isActive
                    ? 'text-white'
                    : 'hover:text-white'
                  : isActive
                    ? 'text-primary'
                    : 'hover:text-primary',
              ].join(' ')}
            >
              {LABELS[lng].short}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}
